import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import TherapyService from "../service/TherapyService";
import { isPregnancy } from "./TherapyPicker";
import "../styles/therapyPicker.scss";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * "2026-11-20…" -> "20 Nov 2026", the server's own wording
 * (api-modular-service formats this field "02 Jan 2006") and legacy bridge's.
 * Read off the calendar day in the string, never through a Date: the value is
 * a day stored at UTC midnight, and a local-time conversion could move it.
 * "" when there is nothing readable.
 */
export const formatDueDate = (raw) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(raw || ""));
  const month = m && MONTHS[Number(m[2]) - 1];
  return month ? `${m[3]} ${month} ${m[1]}` : "";
};

/**
 * "Expected delivery date - 20 Nov 2026", under the preferred-languages line,
 * for a patient on Pregnancy therapy. Legacy bridge's patient screen showed it;
 * the record header did not (product, 2026-10-13).
 *
 * It reads the endpoint the therapy picker WRITES before moving a patient onto
 * Pregnancy (TherapyService.deliveryDate), and reads it again whenever the
 * therapy changes, so a date entered in that prompt shows as soon as the chip
 * turns to Pregnancy. Off Pregnancy, with no date on file, or when the read
 * fails, nothing renders: the line states a fact or is absent.
 */
const DeliveryDateLine = ({ patientId, therapyTag, therapyLabel }) => {
  const pregnant = isPregnancy({ tag: therapyTag, text: therapyLabel });
  const [date, setDate] = useState("");
  const service = useRef(new TherapyService()).current;

  useEffect(() => {
    setDate("");
    if (!pregnant || !patientId) return undefined;
    let live = true;
    service.deliveryDate(
      patientId,
      (raw) => {
        if (live) setDate(formatDueDate(raw));
      },
      () => {}
    );
    return () => {
      live = false;
    };
  }, [pregnant, patientId, therapyTag, service]);

  if (!pregnant || !date) return null;
  return <p className="ph-due-date">Expected delivery date - {date}</p>;
};

DeliveryDateLine.propTypes = {
  patientId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** The current therapy's tag ("9" is Pregnancy). */
  therapyTag: PropTypes.string,
  /** The current therapy's name — matched as well, like the picker does. */
  therapyLabel: PropTypes.string,
};

export default DeliveryDateLine;
